import {
    FieldTree,
    PathKind,
    SchemaPath,
    SchemaPathRules,
    ValidationError,
    validateTree,
} from '@angular/forms/signals';
import {
    spanConflictSummary,
    SpanConflict,
    SpanConflictMessage,
    spanEpoch,
    spanFieldsValue,
    TimeSpanFields,
    TimeSpanItem,
} from './chronologically-ordered.rule';

/** Options of the {@link withinSpan} rule. */
export interface WithinSpanConfig {
    /** builds the message for each conflict found, on both ends of a disagreement */
    message?: SpanConflictMessage;
}

/** An outer span and the items that must lie inside it, as the fields that edit them. */
export interface SpanFields {
    /** the two fields bracketing the outer span */
    span: TimeSpanFields;
    /** the fields of each contained item */
    items: readonly TimeSpanFields[];
}

/**
 * Every way a collection of time spans reaches outside an outer span: an item beginning before the
 * span starts, and an item ending after it ends.
 *
 * **Both ends of a disagreement are reported.** An item outside the span is not the item's fault or
 * the bound's — the two values contradict each other, and either one could be the one to change.
 * Marking only the item would leave a technician who has just mistyped the start of work looking at
 * a red row they consider correct. The bound is reported once however many items disagree with it,
 * so it carries one message rather than a repetition of the same one; its conflict is the one with
 * no `index`.
 *
 * Each bound is enforced on its own, so an unset end does not stop the start being checked, and an
 * unset instant on either side is passed over — an empty value is a `required` question.
 *
 * @param span the outer span the items must lie inside
 * @param items the contained spans
 * @returns one conflict per offending instant, the bounds last
 */
export function findContainmentConflicts(span: TimeSpanItem, items: readonly TimeSpanItem[]): SpanConflict[] {
    const spanStart = spanEpoch(span.start);
    const spanEnd = spanEpoch(span.end);
    const conflicts: SpanConflict[] = [];
    // The instants the bounds would have to move to for the offenders to fit: the earliest start
    // that reaches back, and the latest end that reaches forward. The epochs are carried alongside
    // so the running comparison does not re-parse what was already parsed.
    let earliestStart: string | undefined;
    let earliestStartEpoch = Infinity;
    let latestEnd: string | undefined;
    let latestEndEpoch = -Infinity;

    items.forEach((item, index) => {
        const start = spanEpoch(item.start);
        const end = spanEpoch(item.end);

        if (spanStart !== undefined && start !== undefined && start < spanStart) {
            conflicts.push({
                index,
                half: 'start',
                kind: 'itemBeforeSpan',
                limit: span.start,
                comparison: 'notBefore',
            });
            if (start < earliestStartEpoch) {
                earliestStart = item.start;
                earliestStartEpoch = start;
            }
        }
        if (spanEnd !== undefined && end !== undefined && end > spanEnd) {
            conflicts.push({
                index,
                half: 'end',
                kind: 'itemAfterSpan',
                limit: span.end,
                comparison: 'notAfter',
            });
            if (end > latestEndEpoch) {
                latestEnd = item.end;
                latestEndEpoch = end;
            }
        }
    });

    if (earliestStart !== undefined) {
        conflicts.push({ half: 'start', kind: 'itemBeforeSpan', limit: earliestStart, comparison: 'notAfter' });
    }
    if (latestEnd !== undefined) {
        conflicts.push({ half: 'end', kind: 'itemAfterSpan', limit: latestEnd, comparison: 'notBefore' });
    }

    return conflicts;
}

/**
 * Signal-forms schema rule requiring every item of a collection to lie inside an outer span:
 * nothing may begin before the span starts, and nothing may end after it ends.
 *
 * The counterpart of `coversSpan`, which asks the opposite question. Coverage is about holes and is
 * blind to overhang — an item reaching outside the span still covers everything it crosses, so a
 * span walked for holes alone accepts items that spill out of it. `chronologicallyOrdered` covers
 * the same ordering between one item and the next; this is what extends it to the two bounds
 * bracketing them, which are not items and so have no predecessor or successor to be compared
 * against.
 *
 * For the timesheet that is the difference between "the working day is accounted for" and "the
 * working day is where the technician says it is": without this rule a start of work can be moved
 * past the first entry, or an entry moved before it, and the timesheet still reads as valid.
 *
 * {@link findContainmentConflicts} is where the comparison itself lives, including why both ends of
 * a disagreement are marked.
 *
 * @param path the field carrying the span and its items
 * @param toFields reads the bounding fields and the fields of each contained item
 * @param config optional message
 */
export function withinSpan<TValue, TPathKind extends PathKind = PathKind.Root>(
    path: SchemaPath<TValue, SchemaPathRules.Supported, TPathKind>,
    toFields: (fieldTree: FieldTree<TValue>) => SpanFields,
    config?: WithinSpanConfig,
): void {
    validateTree(path, (context) => {
        const { span, items } = toFields(context.fieldTree);

        const message = config?.message ?? ((): string => '');
        const conflicts = findContainmentConflicts(spanFieldsValue(span), items.map(spanFieldsValue));
        const errors = conflicts.map((conflict): ValidationError.WithOptionalFieldTree => ({
            kind: conflict.kind,
            message: message(conflict),
            fieldTree: (conflict.index === undefined ? span : items[conflict.index])[conflict.half],
        }));

        // The bounding fields are editable whenever anything is, so this is belt and braces here
        // rather than the necessity it is for an overlap — but which fields happen to be editable is
        // exactly the assumption that should not be load-bearing.
        return conflicts.length === 0 ? errors : [...errors, spanConflictSummary(message(conflicts[0]))];
    });
}
